import { defineStore } from 'pinia';

// Interface for the JWT payload. Define new fields here with appropriate types.
interface JwtPayload {
  exp?: number; // Token expiration time, mandatory for expiry checks
  iat?: number; // Issued at time, optional
  nbf?: number; // Not before time, optional
  iss?: string; // Issuer of the token, optional
  userName?: string; // Custom field for user name
  personId?: string; // Custom field for person ID
}


// Interface defining the state structure in the store
interface UserState {
  token: string | undefined; // JWT token or undefined if not fetched yet
  isTokenExpired: boolean; // True if the token has expired, false otherwise
}

// Definition of the user store using Pinia
export const useUserStore = defineStore('user', {
  state: (): UserState => ({
    token: undefined,
    isTokenExpired: true,
  }),
  getters: {
    isLoggedIn: (state): boolean => !state.isTokenExpired,
    isLoggedOut: (state): boolean =>  state.isTokenExpired,
  },
  actions: {
    setToken(token: string = '') {
      // console.groupCollapsed('JWT Token Fetching');
      this.token = token;
      if (token) {
        localStorage.setItem('tokenV1', token);
        this.decodeToken(token);
        console.log('Token Set:', token);
      } else {
        this.isTokenExpired = true;
        localStorage.removeItem('tokenV1');
        console.log('User is Logged Out (No token)');
      }
      // console.groupEnd();
    },

    decodeToken(token: string) {
      // console.groupCollapsed('JWT Token Processing');
      try {
        const user = this.extractUserFromJWT(token);
        // console.log('Decoded Token:', user);

        if (user && user.exp && Date.now() >= user.exp * 1000) {
          console.warn('Token has expired');
          this.isTokenExpired = true;
          localStorage.removeItem('tokenV1');
        } else if (user) {
          this.isTokenExpired = false;
          // console.log('User Info Set:', JSON.stringify(this.userInfo, null, 2));
        } else {
          console.warn('Invalid token');
          this.isTokenExpired = true;
          localStorage.removeItem('tokenV1');
        }
      } catch (error) {
        console.error('Error decoding token:', error);
        this.isTokenExpired = true;
        localStorage.removeItem('tokenV1');
      }
      // console.groupEnd();
    },

    extractUserFromJWT(token: string): JwtPayload | null {
      try {
        const payload = token.split('.')[1];
        const decodedPayload = atob(payload);
        return JSON.parse(decodedPayload);
      } catch {
        return null;
      }
    },

    initializeStore() {
      const token = localStorage.getItem('tokenV1');
      if (token) {
        // console.groupCollapsed('Initializing Store with token');
        this.setToken(token);
        // console.groupEnd();
      } else {
        console.log('No token found in localStorage, store not initialized');
      }
    },
  },
});
